import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  DollarSign,
  ShoppingBag,
  BarChart3,
  TrendingUp,
  Users,
  Palette,
  Tags,
  Droplet,
  FileText,
  Mail,
  Zap,
  Building,
} from "lucide-react";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";

// Import settings section components
import PricingTablesContent from "@/components/settings/PricingTablesContent";
import RetailPricingContent from "@/components/settings/RetailPricingContent";
import AnalyticsContent from "@/components/settings/AnalyticsContent";
import UserManagementContent from "@/components/settings/UserManagementContent";
import BrandingContent from "@/components/settings/BrandingContent";
import QuoteTemplatesContent from "@/components/settings/QuoteTemplatesContent";
import SupplierColoursContent from "@/components/settings/SupplierColoursContent";
import PricingIntelligenceContent from "@/components/settings/PricingIntelligenceContent";

// Settings navigation sections
const SETTINGS_SECTIONS = [
  {
    id: "templates",
    title: "Quote Templates",
    description: "Reusable quote templates",
    icon: FileText,
    color: "text-amber-600",
  },
  {
    id: "pricing",
    title: "Pricing Tables",
    description: "Print method pricing",
    icon: DollarSign,
    color: "text-green-600",
  },
  {
    id: "retail-pricing",
    title: "Retail Pricing",
    description: "Retail pricing rules",
    icon: ShoppingBag,
    color: "text-orange-600",
  },
  {
    id: "supplier-colours",
    title: "Supplier Colours",
    description: "Colour swatches by supplier",
    icon: Droplet,
    color: "text-cyan-600",
  },
  {
    id: "analytics",
    title: "Analytics",
    description: "Business insights",
    icon: BarChart3,
    color: "text-emerald-600",
  },
  {
    id: "pricing-intelligence",
    title: "Pricing Intelligence",
    description: "Pricing patterns",
    icon: TrendingUp,
    color: "text-amber-600",
  },
  {
    id: "users",
    title: "User Management",
    description: "Accounts & permissions",
    icon: Users,
    color: "text-purple-600",
  },
  {
    id: "branding",
    title: "Branding & Integrations",
    description: "Logo, Xero, Gmail",
    icon: Palette,
    color: "text-pink-600",
  },
];

const DEFAULT_SECTION = "analytics";

export default function SettingsHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestedSection = searchParams.get("section");
  const activeSection = requestedSection || DEFAULT_SECTION;

  // Old Settings → Customers links go to the main Customers screen
  useEffect(() => {
    if (requestedSection === "customers") navigate("/customers", { replace: true });
  }, [requestedSection, navigate]);

  const setActiveSection = (sectionId) => {
    setSearchParams({ section: sectionId });
  };

  // Get current section info
  const currentSection = SETTINGS_SECTIONS.find(s => s.id === activeSection)
    || SETTINGS_SECTIONS.find(s => s.id === DEFAULT_SECTION);

  // Render the content for the active section
  const renderContent = () => {
    switch (activeSection) {
      case "templates":
        return <QuoteTemplatesContent />;
      case "pricing":
        return <PricingTablesContent />;
      case "retail-pricing":
        return <RetailPricingContent />;
      case "supplier-colours":
        return <SupplierColoursContent />;
      case "analytics":
        return <AnalyticsContent />;
      case "pricing-intelligence":
        return <PricingIntelligenceContent />;
      case "users":
        return <UserManagementContent />;
      case "branding":
        return <BrandingContent />;
      default:
        return <AnalyticsContent />;
    }
  };

  return (
    <WorkspaceLayout title="Settings">
      <div className="flex h-[calc(100vh-56px)]">
        {/* Left Column - Settings Navigation */}
        <div className="w-56 shrink-0 border-r border-gray-200 bg-gray-50/50 overflow-y-auto">
          <nav className="p-3 space-y-0.5">
            {SETTINGS_SECTIONS.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors ${
                    isActive 
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200" 
                      : "text-gray-600 hover:bg-white hover:text-gray-900"
                  }`}
                  data-testid={`settings-nav-${section.id}`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${isActive ? section.color : "text-gray-400"}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{section.title}</div>
                    {isActive && (
                      <div className="text-[10px] text-gray-500 truncate">{section.description}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Right Column - Settings Content */}
        <div className="flex-1 overflow-y-auto bg-white">
          {renderContent()}
        </div>
      </div>
    </WorkspaceLayout>
  );
}
